import React, { useEffect, useState } from "react";
import styled from "styled-components";
// EduCorners.js-இல் உள்ளவாறு Firebase Imports தொகுக்கப்பட்டுள்ளது
import { collection, getDocs, query, where, or, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "./firebase";
import AppHeader from "./AppHeader";
import { useNavigate } from "react-router-dom";
// EduCorners.js-இல் உள்ள Icon-கள் அனைத்தும் இறக்குமதி செய்யப்படுகின்றன
import { FaMapMarkerAlt, FaGraduationCap, FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaSchool, FaUniversity, FaUserTag } from 'react-icons/fa';

// தேவையான default image URL-களை இறக்குமதி செய்கிறோம்
import defaultMaleProfile from './bdp.jpg';
import defaultFemaleProfile from './gdp.jpg'; 
import defaultCover from './coverimage1.jpg'; 

// ---------- Styled Components ----------
const Container = styled.div`
  background-color: #f5f7fa;
  min-height: 100vh;
  padding-top: 10px;
  padding-bottom: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #1a202c;
  margin-bottom: 40px;
  font-weight: 700;
  text-align: center;
  padding: 0 20px;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  width: 95%;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

// **திருத்தம் செய்யப்பட்ட பகுதி (Cover Image)**
const CoverImageWrapper = styled.div`
  width: 100%;
  
  /* உயரத்தை அதிகரிக்க 315-ஐ 400-ஆக மாற்றியுள்ளேன்.
     இன்னும் உயரம் வேண்டுமென்றால் 400-ஐ 450 அல்லது 500 ஆக மாற்றலாம்.
  */
  aspect-ratio: 851 / 400; 
  
  position: relative;
  overflow: hidden;
  background-color: #dbeafe;
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  margin-top: -40px;
  display: flex;
  justify-content: center;
`;

const ProfileImage = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  background-color: white;
`;

const CardContent = styled.div`
  padding: 15px 20px 20px 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Name = styled.h3`
  font-size: 1.25rem;
  color: #1a202c;
  margin-bottom: 5px;
  font-weight: 600;
`;

const Profession = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  svg {
    margin-right: 5px;
    color: #3b82f6;
    min-width: 16px;
  }
`;

const Location = styled.p`
  font-size: 0.9rem;
  color: #4a5568;
  margin-bottom: 20px;
  display: block; 
  text-align: center;

  svg {
    margin-right: 5px;
    color: #9ca3af;
    min-width: 16px; 
    vertical-align: middle;
  }
`;

const ConnectButton = styled.button`
  width: 85%;
  padding: 10px;
  background-color: ${(props) => 
    props.$status === 'requested' ? '#f97316' : 
    '#3b82f6' 
  };
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: ${(props) => (props.$status === 'requested' ? 'not-allowed' : 'pointer')}; 
  margin-bottom: 20px;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${(props) => 
      props.$status === 'requested' ? '#ea580c' :
      '#2563eb' 
    };
  }

  svg {
    margin-right: 8px;
  }
`;

const DefaultMessage = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  margin-top: 30px;
`;

// -------------------------------------
//        * UTILITY FUNCTION *
// -------------------------------------

const getDisplayDetails = (profile) => {
    let profession = profile.mainRole || 'User';
    let location = `${profile.city || 'Unknown'}, ${profile.country || 'Unknown'}`;
    let professionIcon = FaUserTag;
    let locationIcon = FaMapMarkerAlt;

    const mainRoleLower = (profile.mainRole || '').toLowerCase();
    const subRoleLower = (profile.subRole || '').toLowerCase();
    const studentType = profile.studentType || '';

    if (mainRoleLower === 'educator') {
        profession = profile.subRole || 'Educator';
        professionIcon = FaGraduationCap;
        
        const educatorDetails = profile.schoolTeacherDetails || profile.collegeUniversityEducatorDetails;
        if (educatorDetails) {
            if (subRoleLower === 'teacher' && educatorDetails.institutionName) {
                location = educatorDetails.institutionName; 
                locationIcon = FaSchool;
            } else if (['professor', 'lecturer', 'assistant professor'].includes(subRoleLower) && educatorDetails.institutionName) {
                location = `${educatorDetails.institutionName} (${educatorDetails.branchDepartment || 'Dept.'})`; 
                locationIcon = FaUniversity;
            }
        }
    } 
    else if (mainRoleLower === 'student') {
        profession = studentType || 'Student';
        professionIcon = FaGraduationCap;

        if (studentType === 'School Student' && profile.schoolStudentDetails?.schoolName) {
            location = `${profile.schoolStudentDetails.schoolName} (${profile.schoolStudentDetails.grade || 'Grade'})`;
            locationIcon = FaSchool;
        } else if (studentType === 'College Student' && profile.collegeStudentDetails?.collegeName) {
            location = `${profile.collegeStudentDetails.collegeName} (${profile.collegeStudentDetails.course || 'Course'})`;
            locationIcon = FaUniversity;
        }
    } 
    else if (mainRoleLower === 'general user') {
        profession = 'General User';
        professionIcon = FaUserTag;
    }

    return { profession, location, professionIcon, locationIcon };
};


// -------------------------------------
//        * CONNECT LOGIC *
// -------------------------------------
const sendConnectionRequest = async (receiverId, receiverName, updateStatus) => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    alert("Please login first!");
    updateStatus('default');
    return;
  }
  
  if (currentUser.uid === receiverId) {
      alert("You cannot send a connection request to yourself.");
      updateStatus('default');
      return;
  }

  try {
    const requestRef = await addDoc(collection(db, "connectionRequests"), {
      senderId: currentUser.uid,
      receiverId: receiverId,
      status: "pending",
      timestamp: serverTimestamp(),
      senderEmail: currentUser.email,
      senderName: currentUser.displayName || "A user",
    });

    await addDoc(collection(db, "notifications"), {
      userId: receiverId,
      message: `${currentUser.displayName || currentUser.email} sent you a connection request.`,
      type: "connection_request",
      requestId: requestRef.id,
      senderId: currentUser.uid,
      timestamp: serverTimestamp(),
    });

    updateStatus('requested');
    alert(`Connection request sent to ${receiverName}`);
  } catch (error) {
    console.error("Error sending connection request: ", error);
    alert("Failed to send connection request. Please try again.");
    updateStatus('default');
  }
};


// -------------------------------------
//          MAIN COMPONENT
// -------------------------------------
const StuCorners = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [isAuthReady, setIsAuthReady] = useState(false);
  const navigate = useNavigate();

  const updateStudentStatus = (studentId, status) => {
    setConnectionStatuses(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const fetchStudentsAndStatuses = async (currentUser) => {
    setLoading(true);

    try {
      const studentsQuery = query(
        collection(db, "profiles"),
        or(
          where("mainRole", "==", "Student"),
          where("student Type", "==", "College Student")
        )
      );

      const studentSnapshot = await getDocs(studentsQuery);
      let fetchedStudents = studentSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      
      let filteredStudents = currentUser 
          ? fetchedStudents.filter(student => student.id !== currentUser.uid)
          : fetchedStudents;

      filteredStudents.sort((a, b) => {
          if (!a.fullName) return 1;
          if (!b.fullName) return -1;
          
          const nameA = a.fullName.toUpperCase();
          const nameB = b.fullName.toUpperCase();
          
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0; 
      });

      setStudents(filteredStudents);

      if (currentUser) {
        const requestsQuery = query(
          collection(db, "connectionRequests"),
          where("senderId", "==", currentUser.uid),
          where("status", "in", ["pending", "accepted", "rejected"])
        );

        const requestsSnapshot = await getDocs(requestsQuery);
        const initialStatuses = {};

        requestsSnapshot.docs.forEach(doc => {
          const requestData = doc.data();
          let status;
          if (requestData.status === 'accepted') {
            status = 'connected';
          } else if (requestData.status === 'rejected') {
            status = 'rejected';
          } else {
            status = 'requested'; 
          }
          initialStatuses[requestData.receiverId] = status;
        });

        const defaultStatuses = filteredStudents.reduce((acc, student) => {
          acc[student.id] = 'default';
          return acc;
        }, {});

        setConnectionStatuses({ ...defaultStatuses, ...initialStatuses });
      } else {
        const defaultStatuses = filteredStudents.reduce((acc, student) => {
          acc[student.id] = 'default';
          return acc;
        }, {});
        setConnectionStatuses(defaultStatuses);
      }

    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
        setIsAuthReady(true);
        fetchStudentsAndStatuses(user);
    });

    return () => unsubscribe();
  }, []);
  
  
  const handleProfileClick = (studentId, studentName, status) => {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      alert("Please login to view profiles.");
      return;
    }
    
    if (status === 'requested') {
      alert(`You have to wait until ${studentName} accepts your request.`);
      return;
    } else if (status === 'default' || status === 'rejected') { 
        alert(`You need to connect with ${studentName} to view their profile.`);
        return;
    }

    navigate(`/profileview/${studentId}`);
  };


  return (
    <>
      <AppHeader />

      <Container>
        <Title>
          <span role="img" aria-label="bag">🎒</span> StuCorners — Find Your Student
        </Title>

        {loading || !isAuthReady ? (
          <DefaultMessage>Loading student profiles...</DefaultMessage>
        ) : students.length === 0 ? (
          <DefaultMessage>No student profiles found (or only your profile exists).</DefaultMessage>
        ) : (
          <ProfileGrid>
            {students.map((student) => {
              const status = connectionStatuses[student.id] || 'default';
              const isCurrentUser = auth.currentUser && student.id === auth.currentUser.uid;
              
              const { profession, location, professionIcon: ProfessionIcon, locationIcon: LocationIcon } = getDisplayDetails(student);

              const isMale = (student.gender || '').toLowerCase() === 'male';
              const profileImageSrc = student.profileImageUrl 
                  ? student.profileImageUrl 
                  : isMale ? defaultMaleProfile : defaultFemaleProfile;

              const coverImageSrc = student.coverImageUrl 
                  ? student.coverImageUrl 
                  : defaultCover; 

              let buttonText = isCurrentUser ? 'Your Profile' : 'Connect';
              let Icon = null;

              if (status === 'requested') {
                buttonText = 'Requested';
                Icon = FaHourglassHalf;
              } else if (status === 'connected') {
                buttonText = 'Connected';
                Icon = FaCheckCircle;
              } else if (status === 'rejected') {
                buttonText = 'Connect Again'; 
                Icon = FaTimesCircle;
              }

              const canSendRequest = status !== 'requested' && auth.currentUser && !isCurrentUser;

              return (
                <ProfileCard 
                    key={student.id}
                    onClick={() => handleProfileClick(student.id, student.fullName, status)}
                >
                  <CoverImageWrapper>
                    {student.coverImageUrl ? (
                      <CoverImage
                        src={student.coverImageUrl}
                        alt={`${student.fullName}'s cover`}
                      />
                    ) : (
                      <CoverImage
                          src={coverImageSrc}
                          alt={`${student.fullName}'s default cover`}
                      />
                    )}
                  </CoverImageWrapper>

                  <ProfileImageContainer>
                    <ProfileImage
                      src={profileImageSrc}
                      alt="Profile"
                    />
                  </ProfileImageContainer>

                  <CardContent>
                    <Name>{student.fullName}</Name>

                    <Profession>
                      <ProfessionIcon /> {profession}
                    </Profession>
                    
                    <Location>
                      <LocationIcon /> {location}
                    </Location>

                    <ConnectButton
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (canSendRequest) { 
                            sendConnectionRequest(student.id, student.fullName, (newStatus) => updateStudentStatus(student.id, newStatus));
                        } else if (!auth.currentUser) {
                            alert("Please login to send a connection request.");
                        }
                      }}
                      $status={status}
                      disabled={!canSendRequest && !isCurrentUser} 
                    >
                      {Icon && <Icon />}
                      {buttonText}
                    </ConnectButton>
                  </CardContent>
                </ProfileCard>
              );
            })}
          </ProfileGrid>
        )}
      </Container>
    </>
  );
};

export default StuCorners;